/**
 * Which required fields are missing from a new-student inquiry.
 *
 * Lives outside the page so it can be tested directly. It decides whether a
 * family can sign up at all, and it got that wrong in production.
 *
 * WHAT WENT WRONG. `preferred_location` was in this list, while the teacher
 * matcher offers "Any location" as a selectable option worth the empty string
 * (AvailabilityMatcher Facet renders `<option value="">{anyLabel}</option>`).
 * So a family who chose the option the form itself presented could never
 * submit. Jen Wright hit it on 16 October 2026, tried repeatedly, and emailed
 * info@ saying "the form will not allow us to submit after multiple attempts".
 *
 * Location is NOT required. The studio assigns the venue at booking, blank
 * already means "any" (15 of 72 production inquiries carry no location), and a
 * blocked signup costs the studio a student.
 */
export interface InquiryRequiredShape {
  student_name?: string | null;
  student_dob?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  relationship_to_student?: string | null;
  instrument?: string | null;
  /**
   * 'yes' | 'no' | ''. OPTIONAL IN SUBSTANCE, REQUIRED AS AN ANSWER: either
   * choice submits, blank does not. Both answers are one click and neither
   * affects the student's place, which the consent text says in as many
   * words, so this blocks nobody who reads it.
   *
   * It is here because silence was the whole problem. 76 live students have
   * no recorded answer and nobody can tell who was asked, and a question a
   * family can scroll past reproduces exactly that.
   */
  photo_release?: string | null;
}

const REQUIRED: { key: keyof InquiryRequiredShape; label: string }[] = [
  { key: 'student_name', label: "the student's name" },
  { key: 'student_dob', label: "the student's date of birth" },
  { key: 'contact_name', label: 'your name' },
  { key: 'contact_email', label: 'your email address' },
  { key: 'contact_phone', label: 'your phone number' },
  { key: 'relationship_to_student', label: 'your relationship to the student' },
  { key: 'instrument', label: 'the instrument' },
  { key: 'photo_release', label: 'whether we may photograph your student' },
];

/** Human-readable labels of everything still missing. Empty means ready to submit. */
export function missingRequiredFields(form: InquiryRequiredShape): string[] {
  return REQUIRED.filter(({ key }) => !String(form[key] ?? '').trim()).map((f) => f.label);
}

export function inquiryIsSubmittable(form: InquiryRequiredShape): boolean {
  return missingRequiredFields(form).length === 0;
}

/** Names what is actually missing, rather than listing fields the family already filled. */
export function missingFieldsMessage(missing: string[]): string {
  if (missing.length === 0) return '';
  if (missing.length === 1) return `Please add ${missing[0]} before submitting.`;
  const last = missing[missing.length - 1];
  return `Please add ${missing.slice(0, -1).join(', ')} and ${last} before submitting.`;
}
